import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { Images } from '../assets/assets';
import SvgIcon from '../components/Shared/SvgIcon/SvgIcon';
import scanService from '../services/scanService';

export default function Insights() {
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forecastData, setForecastData] = useState(null);
  const [activeTarget, setActiveTarget] = useState('edss');

  useEffect(() => {
    const patientData = sessionStorage.getItem('selectedPatient');
    if (!patientData) {
      navigate('/admin');
      return;
    }

    try {
      const patient = JSON.parse(patientData);
      setSelectedPatient(patient);
      fetchLatestForecast(patient);
    } catch (e) {
      console.error('Error parsing patient data:', e);
      navigate('/admin');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLatestForecast = async (patient) => {
    setLoading(true);
    try {
      const patientId = patient._id || patient.id;
      if (!patientId) {
        setLoading(false);
        return;
      }
      const scansResponse = await scanService.getScansByCustomer(patientId);
      const scans = (scansResponse.success && Array.isArray(scansResponse.data)) ? scansResponse.data : [];
      if (scans.length === 0) {
        setLoading(false);
        return;
      }

      // Find scan with best forecast data (has values and images)
      let bestScan = null;
      let bestScore = 0;

      for (const scan of scans) {
        const analysis = scan.analysisResult;
        const forecast = analysis?.progressionForecast || {};
        const explanations = analysis?.forecastExplanations || {};
        const consolidated = analysis?.consolidated_results?.[0];
        const assets = consolidated?.assetPaths || {};

        // Score based on available data
        let score = 0;
        if (typeof forecast.predictedChangeInEdssScore === 'number') score += 2;
        if (typeof forecast.predictedChangeInTotalLesionCount === 'number') score += 1;
        if (typeof forecast.predictedChangeInTotalLesionVolume_mm3 === 'number') score += 1;
        if (typeof forecast.predictedChangeInGreyMatterVolume_mm3 === 'number') score += 1;
        if (assets.shapPlotForecast_EDSS) score += 2;
        if (assets.shapPlotForecast_LesionVolWM) score += 1;
        if (assets.shapPlotForecast_LesionCount) score += 1;
        if (assets.shapPlotForecast_GreyMatterVol) score += 1;
        if (explanations && Object.keys(explanations).length > 0) score += 2;

        if (score > bestScore) {
          bestScore = score;
          bestScan = scan;
        }
      }

      if (!bestScan) {
        setLoading(false);
        return;
      }

      const analysis = bestScan.analysisResult;
      const consolidated = analysis?.consolidated_results?.[0];
      const forecast = analysis?.progressionForecast || {};
      const explanations = analysis?.forecastExplanations || {};
      const assets = consolidated?.assetPaths || {};

      setForecastData({
        timepoint: analysis?.timepoint || bestScan.timepoint,
        forecast,
        explanations,
        shap: {
          edss: assets.shapPlotForecast_EDSS,
          lesionVol: assets.shapPlotForecast_Volume,
          lesionCount: assets.shapPlotForecast_Count,
          greyMatter: assets.shapPlotForecast_GreyMatterVolume,
        },
        baselineEdss: consolidated?.patientInformation?.edssScore,
        baselineCount: consolidated?.biomarkers?.totalLesionCount,
        baselineVolume: consolidated?.biomarkers?.totalLesionVolume_mm3,
        baselineGreyMatter: consolidated?.biomarkers?.greyMatterVolume_mm3,
      });
    } catch (err) {
      console.error('Failed to load forecast data', err);
    } finally {
      setLoading(false);
    }
  };

  const targets = useMemo(() => {
    const f = forecastData?.forecast || {};

    const targetsArray = [
      {
        id: 'edss',
        label: 'EDSS Score',
        change: typeof f.predictedChangeInEdssScore === 'number' ? f.predictedChangeInEdssScore : null,
        unit: '',
      },
      {
        id: 'lesionCount',
        label: 'Lesion Count',
        change: typeof f.predictedChangeInTotalLesionCount === 'number' ? f.predictedChangeInTotalLesionCount : null,
        unit: '',
      },
      {
        id: 'lesionVolume',
        label: 'Lesion Volume',
        change: typeof f.predictedChangeInVolume === 'number' ? (f.predictedChangeInVolume / 1000) : null,
        unit: 'mL',
      },
      {
        id: 'greyMatter',
        label: 'Grey Matter',
        change: typeof f.predictedChangeInGreyMatterVolume_mm3 === 'number' ? (f.predictedChangeInGreyMatterVolume_mm3 / 1000) : null,
        unit: 'mL',
      },
    ];

    return targetsArray;
  }, [forecastData]);

  const selectedTarget = targets.find(t => t.id === activeTarget);
  const predictedValue = useMemo(() => {
    if (!forecastData) return 'N/A';

    const change = selectedTarget?.change;
    if (change === 'N/A' || change === null || change === undefined) return 'N/A';

    // For EDSS, add to baseline EDSS score
    if (activeTarget === 'edss') {
      if (typeof forecastData.baselineEdss === 'number' && typeof change === 'number') {
        return (forecastData.baselineEdss + change).toFixed(1);
      }
      return 'N/A';
    }

    // For lesion count, we need to add to baseline count
    if (activeTarget === 'lesionCount') {
      const baselineCount = forecastData.baselineCount || 0;
      if (typeof change === 'number') {
        return (baselineCount + change).toFixed(1);
      }
      return 'N/A';
    }

    // For lesion volume, we need to add to baseline volume
    if (activeTarget === 'lesionVolume') {
      const baselineVolume = forecastData.baselineVolume || 0;
      if (typeof change === 'number') {
        return ((baselineVolume + change) / 1000).toFixed(1) + ' mL';
      }
      return 'N/A';
    }

    // For grey matter, we need to add to baseline grey matter volume
    if (activeTarget === 'greyMatter') {
      const baselineGreyMatter = forecastData.baselineGreyMatter || 0;
      if (typeof change === 'number') {
        return ((baselineGreyMatter + change) / 1000).toFixed(1) + ' mL';
      }
      return 'N/A';
    }

    // For other targets, show the predicted change directly
    if (typeof change === 'number') {
      return change.toFixed(1);
    }

    return 'N/A';
  }, [forecastData, activeTarget, selectedTarget]);

  const shapImage = useMemo(() => {
    if (!forecastData) return null;
    switch (activeTarget) {
      case 'edss':
        return forecastData.shap.edss;
      case 'lesionVolume':
        return forecastData.shap.lesionVol;
      case 'lesionCount':
        return forecastData.shap.lesionCount;
      case 'greyMatter':
        return forecastData.shap.greyMatter;
      default:
        return null;
    }
  }, [forecastData, activeTarget]);

  const featureContributions = useMemo(() => {
    if (!forecastData?.explanations) return { increasing: [], decreasing: [], summary: '' };

    const explanations = forecastData.explanations;
    const increasing = [];
    const decreasing = [];

    // Map activeTarget to explanation key
    const targetKeyMap = {
      'edss': 'EDSS',
      'lesionCount': 'Count',
      'lesionVolume': 'Volume',
      'greyMatter': 'GreyMatterVolume'
    };

    const targetKey = targetKeyMap[activeTarget] || 'EDSS';
    const targetExplanations = explanations[targetKey];

    if (targetExplanations) {
      // Process aggravating factors
      if (targetExplanations.aggravatingFactors && Array.isArray(targetExplanations.aggravatingFactors)) {
        targetExplanations.aggravatingFactors.forEach(factor => {
          if (factor.Feature && typeof factor.SHAP_Value === 'number') {
            increasing.push({
              name: factor.Feature,
              description: `${factor.Feature} contribution`,
              shapValue: factor.SHAP_Value
            });
          }
        });
      }

      // Process mitigating factors
      if (targetExplanations.mitigatingFactors && Array.isArray(targetExplanations.mitigatingFactors)) {
        targetExplanations.mitigatingFactors.forEach(factor => {
          if (factor.Feature && typeof factor.SHAP_Value === 'number') {
            decreasing.push({
              name: factor.Feature,
              description: `${factor.Feature} contribution`,
              shapValue: factor.SHAP_Value
            });
          }
        });
      }
    }

    // Sort by absolute SHAP value
    increasing.sort((a, b) => Math.abs(b.shapValue) - Math.abs(a.shapValue));
    decreasing.sort((a, b) => Math.abs(b.shapValue) - Math.abs(a.shapValue));

    // Generate summary
    let summary = '';
    if (increasing.length > 0 && decreasing.length > 0) {
      const topIncreasing = increasing[0];
      const topDecreasing = decreasing[0];
      summary = `The forecast for ${selectedTarget?.label || 'EDSS Score'} is primarily driven by ${topIncreasing.name} (aggravating factor with SHAP value ${topIncreasing.shapValue.toFixed(3)}), but is partially offset by ${topDecreasing.name} (mitigating factor with SHAP value ${topDecreasing.shapValue.toFixed(3)}).`;
    } else if (increasing.length > 0) {
      const topIncreasing = increasing[0];
      summary = `The forecast for ${selectedTarget?.label || 'EDSS Score'} is primarily driven by ${topIncreasing.name} (aggravating factor with SHAP value ${topIncreasing.shapValue.toFixed(3)}).`;
    } else if (decreasing.length > 0) {
      const topDecreasing = decreasing[0];
      summary = `The forecast for ${selectedTarget?.label || 'EDSS Score'} is primarily driven by ${topDecreasing.name} (mitigating factor with SHAP value ${topDecreasing.shapValue.toFixed(3)}).`;
    }

    return { increasing, decreasing, summary };
  }, [forecastData, selectedTarget, activeTarget]);

  if (loading) {
    return (
      <div className="analysis">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis">
      <div className='custom-card'>
        <div className='custom-card-header'>
          <div className='custom-card-header-text'>
            <h2>
              <SvgIcon src={Images.InsightsIcon} className="icon" />
              Forecast Insights
            </h2>
            <p>Explainable AI predictions for future disease progression</p>
          </div>
        </div>
        <div className='d-flex align-items-center gap-3'>
          <div className='custom-card w-50'>
            <p className='title mb-1'>Forecast Timepoint</p>
            <p className='mb-0'><b>{forecastData?.timepoint || 'N/A'}</b></p>
          </div>
          <div className='custom-card w-50'>
            <p className='title mb-1'>Key Features</p>
            <p className='mb-0'><b>{forecastData?.explanations ? `${Object.keys(forecastData.explanations).length} baseline predictors` : 'N/A'}</b></p>
          </div>
        </div>
      </div>

      <div className='custom-card mt-4 forecast-target'>
        <div className='custom-card-header'>
          <div className='custom-card-header-text'>
            <h3>Select Forecast Target</h3>
            <p>Choose which metric to analyze and explain</p>
          </div>
        </div>
        <Row className='g-3'>
          {targets.map(t => (
            <Col md={3} sm={6} key={t.id}>
              <div
                className={`custom-card w-100 ${activeTarget === t.id ? 'active' : ''}`}
                onClick={() => setActiveTarget(t.id)}
              >
                <div className='d-flex flex-column text-start'>
                  <span className='title'>{t.label}</span>
                  <span className={t.change !== null && t.change < 0 ? 'text-success' : 'text-danger'}>
                    {t.change !== null ? `${t.change > 0 ? '+' : ''}${t.change.toFixed(1)} ${t.unit}` : 'N/A'}
                  </span>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>

      <Row className='mt-3'>
        <Col md={6}>
          <div className='custom-card'>
            <div className='custom-card-header'>
              <div className='custom-card-header-text'>
                <h3>Predicted Change</h3>
                <p>Expected change in {selectedTarget?.label || 'metric'}</p>
              </div>
            </div>
            <h5 className={`mb-0 ${selectedTarget?.change !== null && selectedTarget.change < 0 ? 'text-success' : 'text-danger'}`}>
              {selectedTarget?.change !== null ? `${selectedTarget.change > 0 ? '+' : ''}${selectedTarget.change.toFixed(1)} ${selectedTarget.unit}` : 'N/A'}
            </h5>
          </div>
        </Col>
        <Col md={6}>
          <div className='custom-card'>
            <div className='custom-card-header'>
              <div className='custom-card-header-text'>
                <h3>Predicted Value</h3>
                <p>Forecasted {activeTarget === 'edss' ? 'EDSS Score' : selectedTarget?.label}</p>
              </div>
            </div>
            <h5 className='mb-0'>{predictedValue ?? 'N/A'}</h5>
          </div>
        </Col>
      </Row>


      <Row className='mt-4'>
        <Col md={6}>
          <div className='custom-card'>
            <div className='custom-card-header'>
              <div className='custom-card-header-text'>
                <h3>Feature Importance (SHAP Analysis)</h3>
                <p>Visual explanation of how each feature contributes to the forecast</p>
              </div>
            </div>
            <div className=''>
              {shapImage ? (
                <img src={shapImage} alt='SHAP' className='img-fluid' />
              ) : (
                <div className='no-data-message'>No SHAP plot available</div>
              )}
            </div>
          </div>
        </Col>

        {/* Factors Increasing Predicted Change */}
        <Col md={6}>
          <div className='custom-card'>
            <div className='custom-card-header'>
              <div className='custom-card-header-text'>
                <h3>Factors Increasing Predicted Change</h3>
                <p>Features pushing the forecast higher</p>
              </div>
            </div>
            <div className=''>
              {featureContributions.increasing.length > 0 ? (
                featureContributions.increasing.map((factor, idx) => (
                  <div key={idx} className='mb-3'>
                    <div className='d-flex justify-content-between align-items-center mb-1'>
                      <span className='fw-bold'>{factor.name}</span>
                      <span className='badge bg-danger'>{factor.shapValue > 0 ? '+' : ''}{factor.shapValue.toFixed(3)}</span>
                    </div>
                    <p className='text-muted small mb-0'>{factor.description}</p>
                  </div>
                ))
              ) : (
                <div className='text-muted'>No increasing factors found</div>
              )}
            </div>
          </div>
        </Col>

        {/* Factors Decreasing Predicted Change */}
        <Col md={6} className='mt-4'>
          <div className='custom-card'>
            <div className='custom-card-header'>
              <div className='custom-card-header-text'>
                <h3>Factors Decreasing Predicted Change</h3>
                <p>Features pushing the forecast lower</p>
              </div>
            </div>
            <div className=''>
              {featureContributions.decreasing.length > 0 ? (
                featureContributions.decreasing.map((factor, idx) => (
                  <div key={idx} className='mb-3'>
                    <div className='d-flex justify-content-between align-items-center mb-1'>
                      <span className='fw-bold'>{factor.name}</span>
                      <span className='badge bg-success'>{factor.shapValue.toFixed(3)}</span>
                    </div>
                    <p className='text-muted small mb-0'>{factor.description}</p>
                  </div>
                ))
              ) : (
                <div className='text-muted'>No decreasing factors found</div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* AI Explanation Summary */}
      {featureContributions.summary && (
        <div className='content-card bg-primary'>
          <h5>AI Explanation Summary</h5>
          <p className='mb-0'>{featureContributions.summary}</p>
        </div>
      )}
    </div>
  );
}

